import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, Pencil } from "lucide-react";
import { fileUrl } from "../lib/api";

export default function CartSheet() {
  const { cart, open, setOpen, updateLine, removeLine, openCustomize } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="bg-zinc-950 border-l border-white/10 text-white w-full sm:max-w-md flex flex-col p-0"
        data-testid="cart-sheet"
      >
        <SheetHeader className="px-6 py-5 border-b border-white/10">
          <SheetTitle className="text-2xl font-black tracking-tight text-white">
            Your Bag
          </SheetTitle>
          <p className="text-sm text-zinc-400">
            {cart.count} {cart.count === 1 ? "item" : "items"}
          </p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {cart.items.length === 0 && (
            <div className="text-center py-20 text-zinc-500" data-testid="empty-cart">
              <p className="text-lg">Your bag is empty</p>
              <p className="text-sm mt-2">Discover hidden gems below</p>
            </div>
          )}

          {cart.items.map((it) => {
            const p = it.product;
            const img = p.image_paths?.[0];
            const unit = typeof it.unit_price === "number" ? it.unit_price : p.price;
            const onSale = unit < p.price;
            const snap = it.snapshot;
            const sel = it.selection;
            const hasCustomization = Boolean(sel && (sel.variation_name || (sel.ingredients || []).length || (sel.modifiers || []).length || sel.special_instructions));
            return (
              <div
                key={it.line_id || p.product_id}
                className="flex gap-3 p-3 rounded-2xl bg-white/[0.04] border border-white/5"
                data-testid={`cart-item-${p.product_id}`}
              >
                <div className="w-20 h-20 rounded-xl overflow-hidden bg-zinc-900 shrink-0">
                  {img && (
                    <img
                      src={fileUrl(img)}
                      alt={p.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{p.name}</p>
                  <p className="text-xs text-zinc-400">
                    @{p.vendor_name}
                    {cart.locations?.[p.vendor_id]?.label && (
                      <span className="text-zinc-500" data-testid={`cart-location-${it.line_id}`}> · {cart.locations[p.vendor_id].label}</span>
                    )}
                  </p>

                  {hasCustomization && (
                    <div className="mt-1.5 space-y-0.5 text-[11px] text-zinc-400" data-testid={`cart-customization-${it.line_id}`}>
                      {snap?.variation_label && (
                        <div className="truncate"><span className="text-zinc-500">Size:</span> {snap.variation_label}</div>
                      )}
                      {(snap?.modifier_labels || []).length > 0 && (
                        <div className="truncate">+ {(snap.modifier_labels || []).join(", ")}</div>
                      )}
                      {(snap?.ingredient_notes || []).length > 0 && (
                        <div className="truncate text-amber-300/80">{(snap.ingredient_notes || []).join(" · ")}</div>
                      )}
                      {snap?.special_instructions && (
                        <div className="italic text-pink-300 truncate">"{snap.special_instructions}"</div>
                      )}
                    </div>
                  )}

                  <p className="font-bold mt-1 flex items-baseline gap-1.5">
                    <span className="text-pink-400">${unit.toFixed(2)}</span>
                    {onSale && (
                      <span className="text-xs text-zinc-500 line-through">${p.price.toFixed(2)}</span>
                    )}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <button
                      onClick={() => updateLine(it.line_id, Math.max(0, it.quantity - 1))}
                      className="w-7 h-7 grid place-items-center rounded-full bg-white/10 hover:bg-white/20"
                      data-testid={`cart-decr-${it.line_id}`}
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-6 text-center text-sm font-semibold">{it.quantity}</span>
                    <button
                      onClick={() => updateLine(it.line_id, it.quantity + 1)}
                      className="w-7 h-7 grid place-items-center rounded-full bg-white/10 hover:bg-white/20"
                      data-testid={`cart-incr-${it.line_id}`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                    {hasCustomization && (
                      <button
                        onClick={() => { setOpen(false); openCustomize(p.product_id); }}
                        className="ml-auto px-2.5 py-1 rounded-full text-[11px] bg-white/5 hover:bg-white/15 text-zinc-300 flex items-center gap-1"
                        data-testid={`cart-edit-${it.line_id}`}
                      >
                        <Pencil className="w-3 h-3" /> Add another
                      </button>
                    )}
                    <button
                      onClick={() => removeLine(it.line_id)}
                      className={`${hasCustomization ? "" : "ml-auto"} w-8 h-8 grid place-items-center rounded-full text-zinc-400 hover:text-red-400`}
                      data-testid={`cart-remove-${it.line_id}`}
                      aria-label="Remove item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-6 py-5 border-t border-white/10 space-y-4">
          <div className="flex justify-between text-lg">
            <span className="text-zinc-400">Total</span>
            <span className="font-black text-2xl" data-testid="cart-total">
              ${cart.total.toFixed(2)}
            </span>
          </div>
          <Button
            disabled={cart.items.length === 0}
            onClick={goCheckout}
            className="w-full h-14 rounded-full text-base font-bold bg-pink-500 hover:bg-pink-400 text-white glow-pink disabled:opacity-40 disabled:bg-zinc-800 disabled:glow-pink-none"
            data-testid="checkout-btn"
          >
            Checkout
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
